// assets/gcity-api-integration.js
// Canonical frontend execution owner.
// Rule: do not create assets/gcity-delivery-client.js.
// Repo A owns UI / CTA / dashboard display / executor envelope only.
// Repo B owns job truth / review truth / GFun truth / download gate truth.

(function (w, d) {
  'use strict';

  // ---------------------------------------------------------------------------
  // #0 — Runtime guard
  // Prevent execution when browser globals are unavailable.
  // This file should run only once in the Shopify storefront browser runtime.
  // ---------------------------------------------------------------------------

  if (!w || !d) return;

  // ---------------------------------------------------------------------------
  // #1 — Governance constants
  // These values define the frontend execution contract.
  // Repo A may package an envelope, but Repo B remains the final truth authority.
  // One var per runtime line is kept intentionally for easier diff review.
  // ---------------------------------------------------------------------------

  var VERSION = '2026.05.21-candidate.7-runtime-p0';
  var CORE_EXECUTE_ROUTE = '/apps/gcity-api/core/execute';
  var LEGACY_DELIVERY_TYPES = ['preview', 'download'];

  var API_TIERS = {
    DAILY: 'daily_platform',
    PRO: 'pro_property',
    PROFESSIONAL: 'professional_technical',
    SYSTEM: 'system',
  };

  var API_TIER_VALUES = [API_TIERS.DAILY, API_TIERS.PRO, API_TIERS.PROFESSIONAL, API_TIERS.SYSTEM];

  var ALLOWED_API_FAMILIES = ['core', 'property', 'content', 'tender', 'spatial', 'data', 'map3d'];

  var DELIVERY_TYPES = [
    'tool',
    'report',
    'api',
    'content',
    'service',
    'credit',
    'learning_path',
    'platform',
    'preview',
    'download',
  ];

  var DELIVERY_METHODS = [
    'instant',
    'pdf',
    'download',
    'email',
    'human',
    'dashboard',
    'auto_topup',
    'hybrid',
    'inline',
    'async_job',
    'multipart',
    'polling',
    'intake',
    'manual_review',
  ];

  var RESULT_SURFACES = [
    'panel',
    'workspace',
    'report',
    'download',
    'dashboard',
    'history',
    'email_only',
    'contact_only',
  ];

  var FAMILY_ROUTE_PREFIX = {
    core: '/apps/gcity-api/core',
    property: '/apps/gcity-api/property',
    content: '/apps/gcity-api/content',
    tender: '/apps/gcity-api/tender',
    spatial: '/apps/gcity-api/spatial',
    data: '/apps/gcity-api/data',
    map3d: '/apps/gcity-api/map3d',
  };

  var WORKER_OWNER_HINTS = {
    core: 'gcity-core-api',
    property: 'gproperty-api',
    content: 'little-g-generator',
    tender: 'gtender-api',
    spatial: 'spatial-entry-api',
    data: 'hk-realtime-data-api',
    map3d: 'open3dmap',
  };

  var API_FAMILY_ALIASES = {
    core: 'core',
    'gcity-core-api': 'core',
    map: 'core',
    maps: 'core',
    decision: 'core',
    meta: 'core',
    service: 'core',
    platform: 'core',
    professional: 'core',
    'gcity-platform': 'core',
    'gcity-platform-router': 'core',
    gcitymap_products_handler: 'core',

    property: 'property',
    gproperty: 'property',
    'gproperty-api': 'property',

    content: 'content',
    generator: 'content',
    littleg: 'content',
    ai: 'content',
    report: 'content',
    'little-g-generator': 'content',

    tender: 'tender',
    gtender: 'tender',
    'gtender-api': 'tender',

    spatial: 'spatial',
    entry: 'spatial',
    'spatial-entry-api': 'spatial',

    data: 'data',
    realtime: 'data',
    hkdata: 'data',
    'hk-realtime-data-api': 'data',

    open3d: 'map3d',
    map3d: 'map3d',
    '3d': 'map3d',
    open3dmap: 'map3d',

    gfun: 'core',
    credit: 'core',
    'gfun-webhook-handler': 'core',
  };

  // ---------------------------------------------------------------------------
  // #2 — Endpoint registry
  // Canonical frontend contract map.
  // Frontend uses this for validation and envelope metadata only.
  // Backend router still decides execution truth and worker dispatch.
  // ---------------------------------------------------------------------------

  var ENDPOINT_REGISTRY = {
    '/api/health': {
      api_family: 'gcity-core-api',
      api_tier: API_TIERS.SYSTEM,
      delivery_type: 'api',
      delivery_method: 'instant',
      engine_type: 'decision_engine',
      result_surface: 'panel',
    },
    '/health': {
      api_family: 'gcity-core-api',
      api_tier: API_TIERS.SYSTEM,
      canonical_endpoint: '/api/health',
      delivery_type: 'api',
      delivery_method: 'instant',
      engine_type: 'decision_engine',
      result_surface: 'panel',
    },

    '/api/map/search': {
      api_family: 'gproperty-api',
      api_tier: API_TIERS.DAILY,
      delivery_type: 'tool',
      delivery_method: 'instant',
      engine_type: 'map_engine',
      result_surface: 'workspace',
      requires_gfun: false,
    },

    '/api/map/route': {
      api_family: 'gcity-core-api',
      api_tier: API_TIERS.DAILY,
      delivery_type: 'tool',
      delivery_method: 'instant',
      engine_type: 'map_engine',
      result_surface: 'workspace',
    },
    '/api/map/render': {
      api_family: 'gcity-core-api',
      api_tier: API_TIERS.DAILY,
      delivery_type: 'tool',
      delivery_method: 'instant',
      engine_type: 'map_engine',
      result_surface: 'workspace',
    },
    '/api/map/layer': {
      api_family: 'open3dmap',
      api_tier: API_TIERS.DAILY,
      delivery_type: 'tool',
      delivery_method: 'instant',
      engine_type: 'map_engine',
      result_surface: 'workspace',
    },
    '/api/decision/analyze': {
      api_family: 'gcity-core-api',
      api_tier: API_TIERS.DAILY,
      delivery_type: 'tool',
      delivery_method: 'instant',
      engine_type: 'decision_engine',
      result_surface: 'panel',
    },
    '/api/data/realtime': {
      api_family: 'hk-realtime-data-api',
      api_tier: API_TIERS.DAILY,
      delivery_type: 'tool',
      delivery_method: 'instant',
      engine_type: 'data_engine',
      result_surface: 'panel',
    },
    '/api/meta/bundle': {
      api_family: 'gcity-core-api',
      api_tier: API_TIERS.SYSTEM,
      delivery_type: 'api',
      delivery_method: 'instant',
      engine_type: 'decision_engine',
      result_surface: 'panel',
    },
    '/api/result/history': {
      api_family: 'gcity-core-api',
      api_tier: API_TIERS.SYSTEM,
      delivery_type: 'api',
      delivery_method: 'instant',
      engine_type: 'decision_engine',
      result_surface: 'dashboard',
    },
    '/api/result/save': {
      api_family: 'gcity-core-api',
      api_tier: API_TIERS.SYSTEM,
      delivery_type: 'api',
      delivery_method: 'instant',
      engine_type: 'decision_engine',
      result_surface: 'panel',
    },
    '/api/result/status': {
      api_family: 'gcity-core-api',
      api_tier: API_TIERS.SYSTEM,
      delivery_type: 'api',
      delivery_method: 'polling',
      engine_type: 'decision_engine',
      result_surface: 'dashboard',
    },
    '/api/ai/image': {
      api_family: 'little-g-generator',
      api_tier: API_TIERS.DAILY,
      delivery_type: 'tool',
      delivery_method: 'hybrid',
      engine_type: 'content_engine',
      result_surface: 'workspace',
      requires_gfun: true,
    },
    '/api/ai/video': {
      api_family: 'little-g-generator',
      api_tier: API_TIERS.DAILY,
      delivery_type: 'tool',
      delivery_method: 'hybrid',
      engine_type: 'content_engine',
      result_surface: 'workspace',
      requires_gfun: true,
    },
    '/api/ai/text': {
      api_family: 'little-g-generator',
      api_tier: API_TIERS.DAILY,
      delivery_type: 'tool',
      delivery_method: 'instant',
      engine_type: 'content_engine',
      result_surface: 'panel',
      requires_gfun: true,
    },
    '/api/report/generate': {
      api_family: 'little-g-generator',
      api_tier: API_TIERS.DAILY,
      delivery_type: 'report',
      delivery_method: 'pdf',
      engine_type: 'report_engine',
      result_surface: 'download',
      requires_gfun: true,
    },
    '/api/report/property': {
      api_family: 'gproperty-api',
      api_tier: API_TIERS.PRO,
      delivery_type: 'report',
      delivery_method: 'pdf',
      engine_type: 'property_engine',
      result_surface: 'download',
      requires_gfun: true,
    },
    '/api/report/edu': {
      api_family: 'little-g-generator',
      api_tier: API_TIERS.DAILY,
      delivery_type: 'report',
      delivery_method: 'pdf',
      engine_type: 'report_engine',
      result_surface: 'download',
      requires_gfun: true,
    },
    '/api/report/planner': {
      api_family: 'little-g-generator',
      api_tier: API_TIERS.DAILY,
      delivery_type: 'report',
      delivery_method: 'pdf',
      engine_type: 'report_engine',
      result_surface: 'download',
      requires_gfun: true,
    },
    '/api/delivery/jobs/create': {
      api_family: 'gcity-core-api',
      api_tier: API_TIERS.SYSTEM,
      delivery_type: 'api',
      delivery_method: 'async_job',
      engine_type: 'delivery_job',
      result_surface: 'panel',
    },
    '/api/delivery/jobs/status': {
      api_family: 'gcity-core-api',
      api_tier: API_TIERS.SYSTEM,
      delivery_type: 'api',
      delivery_method: 'polling',
      engine_type: 'delivery_status',
      result_surface: 'dashboard',
    },
    '/api/delivery/manual-review/create': {
      api_family: 'gcity-platform',
      api_tier: API_TIERS.PROFESSIONAL,
      delivery_type: 'service',
      delivery_method: 'intake',
      engine_type: 'service_engine',
      result_surface: 'panel',
      requires_entitlement: true,
    },
    '/api/delivery/jobs/download': {
      api_family: 'gcity-core-api',
      api_tier: API_TIERS.SYSTEM,
      delivery_type: 'download',
      delivery_method: 'download',
      engine_type: 'delivery_download',
      result_surface: 'download',
      requires_gfun: true,
    },
    '/pages/gfun-dashboard': {
      api_family: 'core',
      api_tier: API_TIERS.SYSTEM,
      delivery_type: 'credit',
      delivery_method: 'auto_topup',
      engine_type: 'subscription_engine',
      result_surface: 'panel',
    },
    '/api/gfun/balance': {
      api_family: 'core',
      api_tier: API_TIERS.SYSTEM,
      delivery_type: 'credit',
      delivery_method: 'instant',
      engine_type: 'subscription_engine',
      result_surface: 'panel',
      method: 'GET',
    },
    '/api/gfun/apply': {
      api_family: 'core',
      api_tier: API_TIERS.SYSTEM,
      delivery_type: 'credit',
      delivery_method: 'instant',
      engine_type: 'subscription_engine',
      result_surface: 'panel',
    },
    '/api/gfun/consume': {
      api_family: 'core',
      api_tier: API_TIERS.SYSTEM,
      delivery_type: 'credit',
      delivery_method: 'instant',
      engine_type: 'subscription_engine',
      result_surface: 'panel',
    },

    '/api/gfun/deduct': { blocked: true, reason: 'forbidden_frontend_gfun_mutation' },

    '/api/gfun/grant': { blocked: true, reason: 'forbidden_frontend_gfun_mutation' },

    '/api/gfun/refund': { blocked: true, reason: 'forbidden_frontend_gfun_mutation' },

    '/api/service/lead': {
      api_family: 'gcity-platform',
      api_tier: API_TIERS.PROFESSIONAL,
      canonical_endpoint: '/api/platform/service-lead',
      delivery_type: 'service',
      delivery_method: 'human',
      engine_type: 'service_engine',
      result_surface: 'panel',
      requires_entitlement: true,
    },
    '/api/platform/service-lead': {
      api_family: 'gcity-platform',
      api_tier: API_TIERS.PROFESSIONAL,
      delivery_type: 'service',
      delivery_method: 'human',
      engine_type: 'service_engine',
      result_surface: 'panel',
      requires_entitlement: true,
    },
    '/api/platform/dashboard': {
      api_family: 'gcity-platform',
      api_tier: API_TIERS.PROFESSIONAL,
      delivery_type: 'api',
      delivery_method: 'dashboard',
      engine_type: 'enterprise_engine',
      result_surface: 'dashboard',
      requires_entitlement: true,
    },
    '/api/platform/execute': {
      api_family: 'gcity-platform',
      api_tier: API_TIERS.PROFESSIONAL,
      delivery_type: 'api',
      delivery_method: 'dashboard',
      engine_type: 'enterprise_engine',
      result_surface: 'dashboard',
      requires_entitlement: true,
    },
    '/api/professional/valuation-review': {
      api_family: 'gcity-platform',
      api_tier: API_TIERS.PROFESSIONAL,
      delivery_type: 'service',
      delivery_method: 'human',
      engine_type: 'service_engine',
      result_surface: 'panel',
      requires_entitlement: true,
    },
    '/api/professional/survey-intake': {
      api_family: 'gcity-platform',
      api_tier: API_TIERS.PROFESSIONAL,
      delivery_type: 'service',
      delivery_method: 'human',
      engine_type: 'service_engine',
      result_surface: 'panel',
      requires_entitlement: true,
    },
    '/api/professional/gis-intake': {
      api_family: 'gcity-platform',
      api_tier: API_TIERS.PROFESSIONAL,
      delivery_type: 'service',
      delivery_method: 'human',
      engine_type: 'service_engine',
      result_surface: 'panel',
      requires_entitlement: true,
    },
    '/api/professional/renewal-intake': {
      api_family: 'gcity-platform',
      api_tier: API_TIERS.PROFESSIONAL,
      delivery_type: 'service',
      delivery_method: 'human',
      engine_type: 'service_engine',
      result_surface: 'panel',
      requires_entitlement: true,
    },
    '/api/tender/search': {
      api_family: 'gtender-api',
      api_tier: API_TIERS.DAILY,
      delivery_type: 'api',
      delivery_method: 'instant',
      engine_type: 'enterprise_engine',
      result_surface: 'workspace',
      method: 'GET',
      requires_gfun: false,
      requires_entitlement: false,
      execution_policy: 'public_read_only',
    },
    '/api/tender/list': { blocked: true, reason: 'gtender_route_not_supported' },
    '/api/tender/submit': { blocked: true, reason: 'formal_submission_not_supported' },

    '/api/location/search': {
      api_family: 'gproperty-api',
      api_tier: API_TIERS.DAILY,
      delivery_type: 'tool',
      delivery_method: 'instant',
      engine_type: 'property_engine',
      result_surface: 'workspace',
      method: 'GET',
      requires_gfun: false,
      requires_entitlement: false,
      execution_policy: 'public_read_only',
    },
    '/api/family-data/status': {
      api_family: 'gproperty-api',
      api_tier: API_TIERS.SYSTEM,
      delivery_type: 'api',
      delivery_method: 'instant',
      engine_type: 'property_engine',
      result_surface: 'dashboard',
      method: 'GET',
      requires_gfun: false,
      requires_entitlement: false,
      execution_policy: 'public_redacted_only',
    },
    '/api/family-data/ingest': {
      blocked: true,
      reason: 'server_secret_only_browser_forbidden',
    },
    '/api/property-data/status': {
      api_family: 'gproperty-api',
      api_tier: API_TIERS.SYSTEM,
      delivery_type: 'api',
      delivery_method: 'instant',
      engine_type: 'property_engine',
      result_surface: 'dashboard',
      method: 'GET',
      requires_gfun: false,
      requires_entitlement: false,
      execution_policy: 'public_redacted_only',
    },
    '/api/property-data/ingest': {
      blocked: true,
      reason: 'server_secret_only_browser_forbidden',
    },
    '/api/property/market-context': {
      api_family: 'gproperty-api',
      api_tier: API_TIERS.PRO,
      delivery_type: 'tool',
      delivery_method: 'instant',
      engine_type: 'property_engine',
      result_surface: 'panel',
      method: 'GET',
      requires_gfun: false,
      requires_entitlement: false,
      execution_policy: 'public_sourced_market_context_only',
    },
    '/api/gproperty/family-fit': {
      api_family: 'gproperty-api',
      api_tier: API_TIERS.PRO,
      delivery_type: 'tool',
      delivery_method: 'instant',
      engine_type: 'property_engine',
      result_surface: 'workspace',
      requires_gfun: false,
      requires_entitlement: false,
      readiness_gate: '/api/family-data/status',
      execution_policy: 'fail_closed_until_serving_ready',
    },
    '/api/gproperty/owner-fit': {
      api_family: 'gproperty-api',
      api_tier: API_TIERS.PRO,
      delivery_type: 'tool',
      delivery_method: 'instant',
      engine_type: 'property_engine',
      result_surface: 'workspace',
      requires_gfun: false,
      requires_entitlement: false,
      readiness_gate: '/api/family-data/status',
      execution_policy: 'fail_closed_until_serving_ready',
    },
    '/api/school-catchment-property/preview': {
      api_family: 'gproperty-api',
      api_tier: API_TIERS.PRO,
      delivery_type: 'tool',
      delivery_method: 'instant',
      engine_type: 'property_engine',
      result_surface: 'workspace',
      requires_gfun: false,
      requires_entitlement: false,
      readiness_gate: '/api/family-data/status',
      execution_policy: 'fail_closed_until_serving_ready',
    },
    '/api/gproperty/viewing-route': {
      api_family: 'gproperty-api',
      api_tier: API_TIERS.PRO,
      delivery_type: 'tool',
      delivery_method: 'instant',
      engine_type: 'property_engine',
      result_surface: 'workspace',
      requires_gfun: true,
    },

    '/api/gproperty/report': {
      api_family: 'gproperty-api',
      api_tier: API_TIERS.PRO,
      delivery_type: 'report',
      delivery_method: 'pdf',
      engine_type: 'property_engine',
      result_surface: 'download',
      requires_gfun: true,
    },
    '/api/gproperty/leads': {
      api_family: 'gproperty-api',
      api_tier: API_TIERS.PROFESSIONAL,
      delivery_type: 'service',
      delivery_method: 'intake',
      engine_type: 'property_engine',
      result_surface: 'panel',
      requires_entitlement: true,
    },
    '/api/property/valuation': {
      api_family: 'gproperty-api',
      api_tier: API_TIERS.PRO,
      delivery_type: 'report',
      delivery_method: 'instant',
      engine_type: 'property_engine',
      result_surface: 'panel',
      requires_gfun: false,
      requires_entitlement: false,
      execution_policy: 'fail_closed_until_formal_valuation_ready',
    },

    '/api/gproperty/history': {
      api_family: 'gproperty-api',
      api_tier: API_TIERS.SYSTEM,
      delivery_type: 'api',
      delivery_method: 'instant',
      engine_type: 'property_engine',
      result_surface: 'dashboard',
      method: 'GET',
    },

    // ── Tender ────────────────────────────────────────────────
    '/api/tender/analyze': {
      api_family: 'gtender-api',
      api_tier: API_TIERS.PRO,
      delivery_type: 'tool',
      delivery_method: 'instant',
      engine_type: 'enterprise_engine',
      result_surface: 'workspace',
      requires_gfun: false,
      requires_entitlement: true,
      execution_policy: 'pro_entitlement',
    },

    // ── Product ───────────────────────────────────────────────
    // P0 — Execution bridge, Shopify Product metafield
    '/api/products/trigger': {
      api_family: 'gcity-core-api',
      api_tier: API_TIERS.SYSTEM,
      delivery_type: 'api',
      delivery_method: 'instant',
      engine_type: 'decision_engine',
      result_surface: 'panel',
    },

    // P0 — Pricing read, Shopify Product metafield
    '/api/products/gfun-cost': {
      api_family: 'gcity-core-api',
      api_tier: API_TIERS.SYSTEM,
      delivery_type: 'api',
      delivery_method: 'instant',
      engine_type: 'decision_engine',
      result_surface: 'panel',
      method: 'GET',
    },

    // ── 3D ───────────────────────────────────────────────────
    // P1 — Visualization, Google + custom tiles
    '/api/3d/render': {
      api_family: 'open3dmap',
      api_tier: API_TIERS.PRO,
      delivery_type: 'tool',
      delivery_method: 'async_job',
      engine_type: 'map_engine',
      result_surface: 'workspace',
      requires_gfun: true,
    },

    // ── Social / Sync ─────────────────────────────────────────
    // P2 — Blog sync, WeChat / Instagram (external)
    // Blocked at frontend — backend platform sync only
    '/api/social/sync': { blocked: true, reason: 'forbidden_backend_only_sync' },

    '/api/generate/text': {
      api_family: 'little-g-generator',
      api_tier: API_TIERS.DAILY,
      delivery_type: 'content',
      delivery_method: 'instant',
      engine_type: 'content_engine',
      result_surface: 'panel',
      requires_gfun: true,
    },

    // P1 — Image gen, Replicate / KIE
    '/api/generate/image': {
      api_family: 'little-g-generator',
      api_tier: API_TIERS.DAILY,
      delivery_type: 'content',
      delivery_method: 'hybrid',
      engine_type: 'content_engine',
      result_surface: 'workspace',
      requires_gfun: true,
    },

    // P0 — Reports, OpenAI + R2 storage
    '/api/report/generate': {
      api_family: 'little-g-generator',
      api_tier: API_TIERS.DAILY,
      delivery_type: 'report',
      delivery_method: 'pdf',
      engine_type: 'report_engine',
      result_surface: 'download',
      requires_gfun: true,
    },
    // ── Finance — Webhooks ────────────────────────────────────
    // P0 — Credit on order paid (webhook only — never called from frontend)
    // ── Webhooks — all blocked at frontend ───────────────────
    '/webhook/orders-paid': { blocked: true, reason: 'forbidden_webhook_endpoint' },
    '/webhook/order-paid': { blocked: true, reason: 'forbidden_webhook_endpoint' },
    '/webhook/refund': { blocked: true, reason: 'forbidden_webhook_endpoint' },
    '/webhook/shopify': { blocked: true, reason: 'forbidden_webhook_endpoint' },

    // P1 — Deduct on refund (webhook only)
    '/webhook/refund': { blocked: true, reason: 'forbidden_webhook_endpoint' },

    // ── Finance — GFun ────────────────────────────────────────
    // P0 — Read only, Shopify Customer metafield
    '/api/gfun/balance': {
      api_family: 'core',
      api_tier: API_TIERS.SYSTEM,
      delivery_type: 'credit',
      delivery_method: 'instant',
      engine_type: 'subscription_engine',
      result_surface: 'panel',
      method: 'GET',
    },
  };

  // ---------------------------------------------------------------------------
  // #3 — Legacy compatibility allowlist
  // Compatibility only. New code must use GCityExecutor.execute(ctx).
  // Unknown /api/* requests are blocked instead of silently routed.
  // ---------------------------------------------------------------------------

  var LEGACY_API_ALLOWLIST = {
    '/api/gfun/balance': makeLegacy(
      'core',
      '/api/gfun/balance',
      'preview',
      'inline',
      'gfun_balance',
      'panel'
    ),
    '/api/gfun/apply': makeLegacy(
      'core',
      '/api/gfun/apply',
      'preview',
      'inline',
      'gfun_apply',
      'panel'
    ),
    '/api/report/generate': makeLegacy(
      'core',
      '/api/report/generate',
      'report',
      'inline',
      'report',
      'report'
    ),
    '/api/deliver/email': makeLegacy(
      'core',
      '/api/deliver/email',
      'preview',
      'email',
      'delivery',
      'panel'
    ),
    '/api/entitlement/check': makeLegacy(
      'core',
      '/api/entitlement/check',
      'preview',
      'inline',
      'entitlement',
      'panel'
    ),
    '/api/map/workspace': makeLegacy(
      'core',
      '/api/map/workspace',
      'preview',
      'inline',
      'map_workspace',
      'workspace'
    ),
    '/api/result/status': makeLegacy(
      'core',
      '/api/result/status',
      'preview',
      'inline',
      'result_status',
      'dashboard'
    ),
  };

  function makeLegacy(apiFamily, endpoint, deliveryType, deliveryMethod, engineType, surface) {
    return {
      api_family: apiFamily,
      canonical_endpoint: endpoint,
      delivery_type: deliveryType,
      delivery_method: deliveryMethod,
      engine_type: engineType,
      result_surface: surface,
    };
  }

  // ---------------------------------------------------------------------------
  // #4 — Normalization helpers
  // Small deterministic helpers used before validate(), route(), and execute().
  // These functions must not call network APIs or mutate user/account state.
  // ---------------------------------------------------------------------------

  function toSnakeCase(value) {
    return String(value || '')
      .replace(/([a-z0-9])([A-Z])/g, function (match, left, right) {
        return left + '_' + right;
      })
      .replace(/[\s-]+/g, '_')
      .toLowerCase();
  }

  function normalizeBoolean(value) {
    return value === true || value === 'true' || value === '1' || value === 1;
  }

  function normalizeNumber(value, fallback) {
    var parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function normalizeApiFamily(family) {
    var raw = String(family || 'gcity-core-api').toLowerCase();
    return API_FAMILY_ALIASES[raw] || raw;
  }

  function normalizeEndpoint(endpoint) {
    var raw = String(endpoint || '').trim();
    if (!raw) return '';

    if (raw.indexOf('http://') === 0 || raw.indexOf('https://') === 0) {
      try {
        var parsed = new URL(raw);
        if (parsed.hostname.indexOf('workers.dev') >= 0) return parsed.pathname || '';
        if (parsed.origin !== w.location.origin) return '';
        return parsed.pathname || '';
      } catch (_) {
        return '';
      }
    }

    return raw.charAt(0) === '/' ? raw : '/' + raw;
  }

  function normalizeEndpointForRegistry(endpoint) {
    var path = normalizeEndpoint(endpoint).replace(new RegExp('/+$'), '');
    if (!path) return '';

    if (path.indexOf('/apps/gcity-api/') === 0) {
      path = '/' + path.replace('/apps/gcity-api/', 'api/');
    }

    return path;
  }

  function getCustomerId() {
    var customer = w.CUSTOMER_DATA || w.customerData || {};
    return (
      customer.id ||
      customer.customer_id ||
      customer.customerId ||
      (w.Shopify && w.Shopify.customer && w.Shopify.customer.id) ||
      null
    );
  }

  function getCustomerBalance() {
    // SEC-2: balance must come from server-rendered data only
    // localStorage read removed — can be spoofed by user
    var candidates = [
      w.CUSTOMER_DATA && w.CUSTOMER_DATA.gfunBalance,
      w.CUSTOMER_DATA && w.CUSTOMER_DATA.gfun_balance,
      w.LITTLEG_SERVER_DATA && w.LITTLEG_SERVER_DATA.gfunBalance,
    ];

    for (var i = 0; i < candidates.length; i += 1) {
      var num = Number(candidates[i]);
      if (!Number.isNaN(num) && num >= 0) return num;
    }

    // SEC-2 compliant fallback: 0, never localStorage
    return 0;
  }

  function hasEntitlement() {
    var data = w.CUSTOMER_DATA || {};

    if (data.hasEntitlement === true || data.isPro === true) return true;

    var tier = String(data.tier || data.subscription_tier || '').toLowerCase();
    if (['pro', 'professional', 'enterprise', 'partner', 'paid'].indexOf(tier) >= 0) return true;

    return false;
  }

  function buildIdempotencyKey(payload) {
    payload = payload || {};
    var seed = [
      String(payload.customerId || payload.customer_id || getCustomerId() || 'guest'),
      String(
        payload.productId || payload.product_id || payload.product_handle || 'unknown-product'
      ),
      String(
        payload.inputHash ||
          payload.prompt ||
          payload.reportType ||
          payload.mode ||
          payload.job_id ||
          'na'
      ),
      String(Math.floor(Date.now() / 60000)),
    ].join('|');

    var hash = 0;
    for (var i = 0; i < seed.length; i += 1) {
      hash = (hash << 5) - hash + seed.charCodeAt(i);
      hash |= 0;
    }
    return 'gcity_' + String(Math.abs(hash));
  }

  function safeJsonParse(value) {
    if (!value) return {};
    if (typeof value === 'object') return value;
    try {
      return JSON.parse(String(value));
    } catch (_) {
      return {};
    }
  }

  function normalizeResultSurface(value) {
    var surface = toSnakeCase(value || 'panel');

    var aliases = {
      result_panel: 'panel',

      inline: 'panel',
      inline_preview: 'panel',
      report_card: 'panel',

      modal: 'panel',
      drawer: 'panel',
      bottom_sheet: 'panel',

      workspace_drawer: 'workspace',
      map_panel: 'workspace',

      pdf_report: 'report',

      download_page: 'download',

      result_history: 'history',

      service_intake: 'contact_only',
    };

    surface = aliases[surface] || surface;

    return RESULT_SURFACES.indexOf(surface) !== -1 ? surface : 'panel';
  }

  function readDataset(element) {
    if (!element || !element.dataset) return {};
    return Object.assign({}, element.dataset);
  }

  // ---------------------------------------------------------------------------
  // #5 — Governance resolution and UX gate
  // This is a frontend UX gate only.
  // It improves messaging and prevents impossible requests, but Repo B remains truth.
  // ---------------------------------------------------------------------------

  function resolveApiTier(value) {
    if (!value) return API_TIERS.DAILY;
    var raw = String(value).trim();
    var upper = raw.toUpperCase();
    if (API_TIERS[upper]) return API_TIERS[upper];
    if (API_TIER_VALUES.indexOf(raw) >= 0) return raw;
    return API_TIERS.DAILY;
  }

  function resolveApiFamily(endpoint, fallback) {
    var normalized = normalizeEndpoint(endpoint);
    var registry = ENDPOINT_REGISTRY[normalized];
    if (registry && registry.api_family) return normalizeApiFamily(registry.api_family);
    return normalizeApiFamily(fallback || 'gcity-core-api');
  }

  function normalizeDeliveryByGovernance(ctx) {
    var normalized = Object.assign({}, ctx || {});
    var registry =
      ENDPOINT_REGISTRY[normalizeEndpointForRegistry(normalized.canonical_endpoint)] || {};

    normalized.delivery_type = toSnakeCase(
      normalized.delivery_type || registry.delivery_type || 'preview'
    );
    normalized.delivery_method = toSnakeCase(
      normalized.delivery_method || registry.delivery_method || 'inline'
    );
    normalized.result_surface = normalizeResultSurface(
      normalized.result_surface || registry.result_surface || 'panel'
    );
    normalized.api_tier = resolveApiTier(normalized.api_tier || registry.api_tier);
    normalized.requires_gfun = normalizeBoolean(
      normalized.requires_gfun != null ? normalized.requires_gfun : registry.requires_gfun
    );
    normalized.requires_entitlement = normalizeBoolean(
      normalized.requires_entitlement != null
        ? normalized.requires_entitlement
        : registry.requires_entitlement
    );

    return normalized;
  }

  function gate(ctx) {
    ctx = normalizeDeliveryByGovernance(ctx || {});

    if (ctx.requires_entitlement && !hasEntitlement()) {
      return {
        ok: false,
        success: false,
        blocked: true,
        reason: 'entitlement_required',
        result_surface: 'panel',
        api_tier: ctx.api_tier,
        worker_owner_hint: ctx.worker_owner_hint,
        service_cta_type: ctx.service_cta_type || 'book_consultation',
      };
    }

    if (ctx.requires_gfun && ctx.gfun_cost > 0 && getCustomerBalance() < ctx.gfun_cost) {
      return {
        ok: false,
        success: false,
        blocked: true,
        reason: 'insufficient_gfun',
        required: ctx.gfun_cost,
        balance: getCustomerBalance(),
        result_surface: 'panel',
        api_tier: ctx.api_tier,
        worker_owner_hint: ctx.worker_owner_hint,
      };
    }

    return { ok: true, success: true };
  }

  // ---------------------------------------------------------------------------
  // #6 — Context builders
  // Convert DOM dataset or product runtime data into executor context.
  // These builders do not execute; they only produce metadata envelopes.
  // ---------------------------------------------------------------------------

  function buildContextFromElement(element, overrides) {
    var ds = readDataset(element);
    return Object.assign(
      {
        api_family: ds.apiFamily || ds.api_family,
        canonical_endpoint: ds.canonicalEndpoint || ds.canonical_endpoint,
        delivery_type: ds.deliveryType || ds.delivery_type,
        delivery_method: ds.deliveryMethod || ds.delivery_method,
        engine_type: ds.engineType || ds.engine_type,
        result_surface: ds.resultSurface || ds.result_surface,
        requires_gfun: normalizeBoolean(ds.requiresGfun || ds.requires_gfun),
        requires_entitlement: normalizeBoolean(ds.requiresEntitlement || ds.requires_entitlement),
        gfun_cost: normalizeNumber(ds.gfunCost || ds.gfun_cost, 0),
        product_type: ds.productType || ds.product_type,
        product_handle: ds.productHandle || ds.product_handle || ds.handle || '',
        product_id: ds.productId || ds.product_id || '',
        data: {},
      },
      overrides || {}
    );
  }

  function buildContextFromProduct(productRuntime, overrides) {
    var product = productRuntime || (w.GCITY_CONFIG && w.GCITY_CONFIG.product) || {};
    return Object.assign(
      {
        api_family: product.apiFamily || product.api_family,
        canonical_endpoint: product.canonicalEndpoint || product.canonical_endpoint,
        delivery_type: product.deliveryType || product.delivery_type,
        delivery_method: product.deliveryMethod || product.delivery_method,
        engine_type: product.engineType || product.engine_type,
        result_surface: product.resultSurface || product.result_surface,
        requires_gfun: normalizeBoolean(product.requiresGfun || product.requires_gfun),
        requires_entitlement: normalizeBoolean(
          product.requiresEntitlement || product.requires_entitlement
        ),
        gfun_cost: normalizeNumber(
          product.gfunCost || product.gfun_cost || product.gfun_consume_per_call,
          0
        ),
        product_type: product.productType || product.product_type,
        product_handle: product.handle || product.product_handle || product.productHandle || '',
        product_id: product.id || product.product_id || product.productId || '',
        data: {},
      },
      overrides || {}
    );
  }

  // ---------------------------------------------------------------------------
  // #7 — Validation and route debugging
  // validate() is the executor contract gate.
  // route() always returns CORE_EXECUTE_ROUTE to preserve backend dispatch authority.
  // ---------------------------------------------------------------------------

  function readCanonicalMeta(endpoint) {
    return ENDPOINT_REGISTRY[normalizeEndpointForRegistry(endpoint)] || null;
  }

  function validate(ctx) {
    var input = Object.assign({}, ctx || {});
    var registryMeta =
      readCanonicalMeta(input.canonical_endpoint || input.canonicalEndpoint || input.endpoint) ||
      {};
    var normalized = Object.assign({}, registryMeta, input, {
      api_family: resolveApiFamily(
        input.canonical_endpoint || input.canonicalEndpoint || input.endpoint,
        input.api_family || input.apiFamily
      ),
      canonical_endpoint: normalizeEndpoint(
        input.canonical_endpoint || input.canonicalEndpoint || input.endpoint
      ),
      delivery_type: toSnakeCase(
        input.delivery_type || input.deliveryType || registryMeta.delivery_type || 'preview'
      ),
      delivery_method: toSnakeCase(
        input.delivery_method || input.deliveryMethod || registryMeta.delivery_method || 'inline'
      ),
      engine_type: toSnakeCase(
        input.engine_type || input.engineType || registryMeta.engine_type || 'general'
      ),
      result_surface: normalizeResultSurface(
        input.result_surface || input.resultSurface || registryMeta.result_surface
      ),
      requires_gfun: normalizeBoolean(
        input.requires_gfun != null
          ? input.requires_gfun
          : input.requiresGfun != null
            ? input.requiresGfun
            : registryMeta.requires_gfun
      ),
      requires_entitlement: normalizeBoolean(
        input.requires_entitlement != null
          ? input.requires_entitlement
          : input.requiresEntitlement != null
            ? input.requiresEntitlement
            : registryMeta.requires_entitlement
      ),
      gfun_cost: normalizeNumber(input.gfun_cost || input.gfunCost, 0),
      product_handle: input.product_handle || input.productHandle || '',
      product_id: input.product_id || input.productId || '',
      api_tier: resolveApiTier(input.api_tier || input.apiTier || registryMeta.api_tier),
      continuation_url: input.continuation_url || input.continuationUrl || null,
      data: input.data || input.payload || {},
    });

    if (registryMeta.blocked)
      throw new Error(registryMeta.reason + ':' + normalized.canonical_endpoint);
    if (ALLOWED_API_FAMILIES.indexOf(normalized.api_family) < 0)
      throw new Error('invalid_api_family:' + normalized.api_family);
    if (!normalized.canonical_endpoint) throw new Error('missing_canonical_endpoint');

    normalized = normalizeDeliveryByGovernance(normalized);
    normalized.worker_owner_hint =
      WORKER_OWNER_HINTS[normalized.api_family] || normalized.api_family;
    normalized.legacy_delivery_type = LEGACY_DELIVERY_TYPES.indexOf(normalized.delivery_type) >= 0;

    if (DELIVERY_TYPES.indexOf(normalized.delivery_type) < 0)
      throw new Error('invalid_delivery_type:' + normalized.delivery_type);
    if (DELIVERY_METHODS.indexOf(normalized.delivery_method) < 0)
      throw new Error('invalid_delivery_method:' + normalized.delivery_method);
    if (RESULT_SURFACES.indexOf(normalized.result_surface) < 0)
      throw new Error('invalid_result_surface:' + normalized.result_surface);
    if (
      normalized.canonical_endpoint.indexOf('/webhook/') === 0 ||
      normalized.canonical_endpoint.indexOf('/api/webhook/') === 0
    )
      throw new Error('forbidden_webhook_endpoint:' + normalized.canonical_endpoint);
    if (
      normalized.canonical_endpoint.indexOf('/gfun/deduct') >= 0 ||
      normalized.canonical_endpoint.indexOf('/gfun/grant') >= 0 ||
      normalized.canonical_endpoint.indexOf('/gfun/refund') >= 0
    )
      throw new Error('forbidden_frontend_gfun_mutation:' + normalized.canonical_endpoint);

    return normalized;
  }

  function debugRoute(context, options) {
    var ctx = validate(context, options || {});

    return {
      route: CORE_EXECUTE_ROUTE,
      canonical_endpoint: ctx.canonical_endpoint,
      api_family: ctx.api_family,
      worker_owner_hint:
        ctx.worker_owner_hint || WORKER_OWNER_HINTS[ctx.api_family] || ctx.api_family,
      api_tier: ctx.api_tier,
      delivery_type: ctx.delivery_type,
      delivery_method: ctx.delivery_method,
      result_surface: ctx.result_surface,
      legacy_delivery_type: ctx.legacy_delivery_type === true,
    };
  }

  function route(context) {
    validate(context);
    return CORE_EXECUTE_ROUTE;
  }

  // ---------------------------------------------------------------------------
  // #8 — Core execution runtime
  // execute() sends JSON envelopes to CORE_EXECUTE_ROUTE.
  // executeMultipart() supports files, CAD, photos, and other Blob/File payloads.
  // ---------------------------------------------------------------------------

  async function executeMultipart(ctx) {
    var validated = validate(Object.assign({}, ctx || {}, { delivery_method: 'multipart' }));
    var formData = new FormData();
    var payload = validated.data || {};

    Object.keys(payload).forEach(function appendPayload(key) {
      var value = payload[key];
      if (value == null) return;
      if (typeof File !== 'undefined' && value instanceof File) return formData.append(key, value);
      if (typeof Blob !== 'undefined' && value instanceof Blob) return formData.append(key, value);
      if (typeof value === 'object') return formData.append(key, JSON.stringify(value));
      formData.append(key, String(value));
    });

    formData.append(
      'context',
      JSON.stringify({
        api_family: validated.api_family,
        canonical_endpoint: validated.canonical_endpoint,
        delivery_type: validated.delivery_type,
        delivery_method: validated.delivery_method,
        engine_type: validated.engine_type,
        result_surface: validated.result_surface,
        api_tier: validated.api_tier,
      })
    );

    var response = await fetch(CORE_EXECUTE_ROUTE, {
      method: 'POST',
      credentials: 'same-origin',
      body: formData,
    });
    return normalizeResponse(response, validated);
  }

  async function normalizeResponse(response, context) {
    var body = null;

    try {
      body = await response.json();
    } catch (error) {
      body = null;
    }

    var envelope = {
      ok: response.ok,
      success: response.ok,
      type: context.delivery_type || 'tool',
      result_surface: normalizeResultSurface(context.result_surface),
      api_tier: context.api_tier,
      worker_owner_hint: context.worker_owner_hint,
      data: body && (body.data || body.result) ? body.data || body.result : body,
      meta: { status: response.status },
      error: null,
    };

    if (!response.ok) {
      envelope.ok = false;
      envelope.success = false;
      envelope.error = (body && (body.error || body.message)) || 'HTTP ' + response.status;
    }

    if (body && typeof body === 'object') {
      if (body.result_surface)
        envelope.result_surface = normalizeResultSurface(body.result_surface);
      if (body.type) envelope.type = body.type;
      if (body.meta) envelope.meta = Object.assign(envelope.meta, body.meta);
      if (body.error) envelope.error = body.error;

      if (body.ok === false || body.success === false) {
        envelope.ok = false;
        envelope.success = false;
      }
    }

    return envelope;
  }

  function resultHistoryUrl(resultId) {
    return '/pages/result-history?result_id=' + encodeURIComponent(resultId || '');
  }

  function render(envelope, context) {
    if (!envelope) return envelope;

    var data = envelope.data || {};

    if (
      (context.result_surface === 'download' || envelope.result_surface === 'download') &&
      data &&
      !data.download_url &&
      (data.result_id || data.resultId)
    ) {
      envelope.continuation_url = resultHistoryUrl(data.result_id || data.resultId);
      envelope.notice = 'download_pending_history_available';
    }

    var targetId =
      context &&
      (context.target_id || context.targetId || context.result_target || context.resultTarget);
    if (!targetId) return envelope;

    var target = d.getElementById(targetId) || d.querySelector(targetId);
    if (!target) return envelope;

    target.textContent =
      typeof envelope.data === 'string'
        ? envelope.data
        : JSON.stringify(envelope.data || envelope, null, 2);

    return envelope;
  }

  async function execute(context, options) {
    options = options || {};

    var ctx;

    try {
      ctx = validate(context, options);
    } catch (error) {
      return {
        ok: false,
        success: false,
        blocked: true,
        reason: error.message || 'invalid_context',
        error: error.message || 'invalid_context',
        result_surface: 'panel',
        data: null,
      };
    }

    if (!options.skipGate) {
      var gateResult = gate(ctx);
      if (!gateResult.ok) return gateResult;
    }

    var payload = Object.assign({}, ctx.data || {}, {
      context: {
        product_handle: ctx.product_handle,
        product_id: ctx.product_id,
        api_family: ctx.api_family,
        worker_owner_hint: ctx.worker_owner_hint,
        api_tier: ctx.api_tier,
        canonical_endpoint: ctx.canonical_endpoint,
        delivery_type: ctx.delivery_type,
        delivery_method: ctx.delivery_method,
        engine_type: ctx.engine_type,
        result_surface: ctx.result_surface,
        ui_mode: ctx.ui_mode,
        product_category: ctx.product_category,
        service_type: ctx.service_type,
        service_active: ctx.service_active,
        service_cta_type: ctx.service_cta_type,
        requires_gfun: ctx.requires_gfun,
        requires_entitlement: ctx.requires_entitlement,
        gfun_cost: ctx.gfun_cost,
        legacy_delivery_type: ctx.legacy_delivery_type,
        idempotency_key: ctx.idempotency_key || ctx.idempotencyKey || buildIdempotencyKey(ctx.data),
      },
    });

    try {
      var response = await fetch(CORE_EXECUTE_ROUTE, {
        method: 'POST',
        credentials: 'same-origin',
        headers: Object.assign({ 'Content-Type': 'application/json' }, ctx.headers || {}),
        body: JSON.stringify(payload),
      });

      return render(await normalizeResponse(response, ctx), ctx);
    } catch (error) {
      return {
        ok: false,
        success: false,
        type: ctx.delivery_type,
        result_surface: ctx.result_surface,
        api_tier: ctx.api_tier,
        worker_owner_hint: ctx.worker_owner_hint,
        data: null,
        meta: {
          network_error: true,
          url: CORE_EXECUTE_ROUTE,
        },
        error: error.message || 'network_error',
      };
    }
  }

  // ---------------------------------------------------------------------------
  // #8.1 — Explicit non-merge decision
  // applyGFun() and run() from candidate.6 are intentionally NOT included here.
  // Reason: Repo A must not own frontend GFun consume/apply orchestration.
  // Repo B owns GFun truth, deduction, ledger, report truth, and download truth.
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // #9 — LittleG executor API
  // Exposes the canonical frontend executor surface.
  // This section only publishes existing runtime functions created in #1-#8.
  // ---------------------------------------------------------------------------

  w.GCityExecutor = w.GCityExecutor || {};

  // #9.1 — Runtime metadata
  w.GCityExecutor.VERSION = VERSION;
  w.GCityExecutor.CORE_EXECUTE_ROUTE = CORE_EXECUTE_ROUTE;
  w.GCityExecutor.ENDPOINT_REGISTRY = ENDPOINT_REGISTRY;
  w.GCityExecutor.WORKER_OWNER_HINTS = WORKER_OWNER_HINTS;

  // #9.2 — Context builders
  w.GCityExecutor.buildContextFromElement = buildContextFromElement;
  w.GCityExecutor.buildContextFromProduct = buildContextFromProduct;

  // #9.3 — Validation and route helpers
  w.GCityExecutor.validate = validate;
  w.GCityExecutor.route = route;
  w.GCityExecutor.debugRoute = debugRoute;

  // #9.4 — Execution and rendering helpers
  w.GCityExecutor.execute = execute;
  w.GCityExecutor.executeMultipart = executeMultipart;
  w.GCityExecutor.normalizeResponse = normalizeResponse;
  w.GCityExecutor.render = render;
  w.GCityExecutor.readMarketContext = async function readMarketContext(options) {
    const context = ENDPOINT_REGISTRY['/api/property/market-context'];
    try {
      const url = new URL(
        'https://api.glhksolution.com/property/property/market-context'
      );

      if (
        options &&
        options.productHandle === 'glg-property-investment-intelligence'
      ) {
        url.searchParams.set('metric', 'price_index');
        url.searchParams.set('property_category', 'private_domestic');
        url.searchParams.set('property_class', 'All Classes');
        url.searchParams.set('limit', '3');
      }

      const response = await fetch(url.href, {
        method: 'GET',
        credentials: 'omit',
        headers: { Accept: 'application/json' },
        signal: options && options.signal
      });

      return await normalizeResponse(response, context);
    } catch (_) {
      return {
        ok: false,
        success: false,
        data: null,
        error: 'market_context_unavailable'
      };
    }
  };

  // #9.5 — Browser smoke test helper
  w.GCityExecutor.contractSelfTest = function contractSelfTest() {
    return {
      ok: true,
      source: 'gcity-api-integration.js',
      execute_route: CORE_EXECUTE_ROUTE,
      endpoint_registry_size: Object.keys(ENDPOINT_REGISTRY || {}).length,
      delivery_types: DELIVERY_TYPES.length,
      delivery_methods: DELIVERY_METHODS.length,
      api_tiers: API_TIER_VALUES.length,
      has_validate: typeof validate === 'function',
      has_execute: typeof execute === 'function',
      has_execute_multipart: typeof executeMultipart === 'function',
      delivery_envelope: w.GCityExecutor.__deliveryEnvelopeInstalled === true,
      app_proxy_only: CORE_EXECUTE_ROUTE.indexOf('/apps/gcity-api/') === 0,
    };
  };

  // #9.6 — Ready flag
  // Ensure any layout-provided fallback stub is cleared so consumers
  // that check __isFallbackStub see the real executor instance.
  try {
    if (w.GCityExecutor && w.GCityExecutor.__isFallbackStub === true) {
      w.GCityExecutor.__isFallbackStub = false;
    }
  } catch (e) {
    // safe noop
  }
  w.GCityExecutor.isReady = true;

  // #9.7 — Attach dependent runtime surfaces
  attachDeliveryEnvelope();
  attachCompatibilityWrappers();
  exposeRuntimeGlobals();

  // ---------------------------------------------------------------------------
  // #10 — CTA delivery envelope bridge
  // Binds only CTA buttons that explicitly opt in through data-lg-delivery-action.
  // It calls GCityExecutor.execute(ctx) and does not call fetch() directly.
  // ---------------------------------------------------------------------------

  function attachDeliveryEnvelope() {
    if (!w.GCityExecutor || w.GCityExecutor.__deliveryEnvelopeInstalled) return;

    // #10.1 — CTA selector and semantic action map
    var DELIVERY_TRIGGER_SELECTOR =
      '.cta-stack-standard a[data-cta-action][data-lg-delivery-action]';
    var DELIVERY_ACTION_ENDPOINTS = Object.freeze({
      'delivery.job.create': '/api/delivery/jobs/create',
      'delivery.job.status': '/api/delivery/jobs/status',
      'delivery.manualReview.create': '/api/delivery/manual-review/create',
      'gfun.verify': '/api/gfun/balance',
      'delivery.download.gate': '/api/delivery/jobs/download',
    });
    var DELIVERY_ALLOWED_ACTIONS = Object.keys(DELIVERY_ACTION_ENDPOINTS);

    // #10.2 — Resolve semantic action to canonical endpoint
    function getDeliveryEndpoint(action, fallbackEndpoint) {
      return DELIVERY_ACTION_ENDPOINTS[action] || fallbackEndpoint || '';
    }

    // #10.3 — Build frontend delivery payload from CTA dataset
    function buildDeliveryPayload(action, trigger) {
      var ds = trigger && trigger.dataset ? trigger.dataset : {};
      var base = {
        product_handle: ds.productHandle || ds.handle || '',
        canonical_endpoint: ds.canonicalEndpoint || '',
        delivery_type: ds.deliveryType || '',
        delivery_method: ds.deliveryMethod || '',
        gfun_cost: normalizeNumber(ds.gfunCost, 0),
      };

      if (action === 'delivery.job.create') {
        return Object.assign({}, base, {
          payload: {
            api_family: ds.apiFamily || '',
            engine_type: ds.engineType || '',
            result_surface: ds.resultSurface || '',
            requires_gfun: normalizeBoolean(ds.requiresGfun),
            requires_entitlement: normalizeBoolean(ds.requiresEntitlement),
            source: 'repo-a-cta-stack',
          },
        });
      }

      if (action === 'delivery.job.status') {
        return {
          job_id: ds.jobId || '',
        };
      }

      if (action === 'delivery.manualReview.create') {
        return {
          job_id: ds.jobId || '',
          product_handle: base.product_handle,
          review_type: ds.reviewType || 'professional_review',
          contact_method: ds.contactMethod || 'platform',
          notes: ds.reviewNotes || '',
        };
      }

      if (action === 'gfun.verify') {
        return {
          job_id: ds.jobId || '',
          product_handle: base.product_handle,
          gfun_cost: base.gfun_cost,
          action: ds.gfunAction || 'consume_request',
          reason: ds.gfunReason || 'delivery_verification',
        };
      }

      if (action === 'delivery.download.gate') {
        return {
          job_id: ds.jobId || '',
        };
      }

      return base;
    }

    // #10.4 — Find the nearest delivery status surface
    function findDeliveryStatusSurface(trigger) {
      var root = trigger && trigger.closest ? trigger.closest('.cta-stack-standard') : null;

      return root ? root.querySelector('[data-lg-delivery-status]') : null;
    }

    // #10.5 — Render frontend status only
    function renderDeliveryStatus(trigger, state) {
      var surface = findDeliveryStatusSurface(trigger);
      if (!surface) return;

      surface.hidden = false;
      surface.dataset.status = state && state.state ? state.state : 'idle';

      surface.textContent = state && state.message ? state.message : '';
    }

    // #10.6 — Convert result envelope into user-facing status copy
    function getDeliveryMessage(action, result) {
      var data = result && result.data ? result.data : result;

      if (action === 'delivery.job.create') {
        return data && data.job_id ? 'Job created. Reference: ' + data.job_id : 'Job request sent.';
      }

      if (action === 'delivery.job.status') {
        return data && data.status ? 'Job status: ' + data.status : 'Status received.';
      }

      if (action === 'delivery.manualReview.create') {
        return data && data.review_id
          ? 'Review intake received. Reference: ' + data.review_id
          : 'Review request received.';
      }

      if (action === 'gfun.verify') {
        return data && data.verified_sufficient
          ? 'GFun balance verified.'
          : 'GFun verification completed.';
      }

      if (action === 'delivery.download.gate') {
        if (data && data.download_eligible) return 'Download gate passed.';
        if (data && data.message) return data.message;
        return 'Download is not available yet.';
      }

      return 'Request completed.';
    }

    // #10.7 — Execute a delivery CTA action through GCityExecutor
    async function executeDeliveryAction(trigger, event) {
      var ds = trigger && trigger.dataset ? trigger.dataset : {};
      var action = ds.lgDeliveryAction || '';

      if (!action || DELIVERY_ALLOWED_ACTIONS.indexOf(action) < 0) return null;

      var endpoint = getDeliveryEndpoint(action, ds.canonicalEndpoint);
      if (!endpoint) {
        renderDeliveryStatus(trigger, {
          state: 'error',
          message: 'Delivery endpoint is not configured.',
        });
        return null;
      }

      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      var payload = buildDeliveryPayload(action, trigger);
      if (!payload.customer_id) {
        renderDeliveryStatus(trigger, {
          state: 'blocked',
          message: 'Please sign in before continuing.',
        });
        return null;
      }

      renderDeliveryStatus(trigger, {
        state: 'loading',
        message: 'Preparing request...',
      });

      var ctx = {
        action: action,
        canonical_endpoint: endpoint,
        method: 'POST',
        api_family: ds.apiFamily || 'gcity-core-api',
        delivery_type: ds.deliveryType || 'preview',
        delivery_method: ds.deliveryMethod || 'inline',
        engine_type: ds.engineType || 'delivery_envelope',
        result_surface: ds.resultSurface || 'panel',
        requires_gfun: normalizeBoolean(ds.requiresGfun),
        requires_entitlement: normalizeBoolean(ds.requiresEntitlement),
        gfun_cost: normalizeNumber(ds.gfunCost, 0),
        product_handle: ds.productHandle || '',
        data: payload,
        source: 'cta-stack-standard',
      };

      try {
        var result = await w.GCityExecutor.execute(ctx);

        renderDeliveryStatus(trigger, {
          state: result && result.ok === false ? 'error' : 'success',
          message: result && result.error ? result.error : getDeliveryMessage(action, result),
        });

        w.dispatchEvent(
          new CustomEvent('gcity:delivery:updated', {
            detail: {
              action: action,
              endpoint: endpoint,
              payload: payload,
              result: result,
              trigger: trigger,
            },
          })
        );

        return result;
      } catch (error) {
        renderDeliveryStatus(trigger, {
          state: 'error',
          message: error && error.message ? error.message : 'Delivery request failed.',
        });

        w.dispatchEvent(
          new CustomEvent('gcity:delivery:error', {
            detail: {
              action: action,
              endpoint: endpoint,
              error: error,
              trigger: trigger,
            },
          })
        );

        return null;
      }
    }

    // #10.8 — Delegated CTA click listener
    d.addEventListener('click', function handleDeliveryCtaClick(event) {
      var target =
        event.target && event.target.closest
          ? event.target.closest(DELIVERY_TRIGGER_SELECTOR)
          : null;

      if (!target) return;
      if (!w.GCityExecutor || typeof w.GCityExecutor.execute !== 'function') return;

      executeDeliveryAction(target, event);
    });

    // #10.9 — Expose delivery bridge QA helpers
    w.GCityExecutor.__deliveryEnvelopeInstalled = true;
    w.GCityExecutor.deliveryActions = DELIVERY_ACTION_ENDPOINTS;
    w.GCityExecutor.executeDeliveryAction = executeDeliveryAction;
    w.GCityExecutor.renderDeliveryStatus = renderDeliveryStatus;
  }

  // ---------------------------------------------------------------------------
  // #11 — Compatibility wrappers
  // Legacy callers remain supported through guarded compatibility helpers.
  // Unknown legacy API routes and raw worker URLs are blocked.
  // ---------------------------------------------------------------------------

  function attachCompatibilityWrappers() {
    w.GCITY_SAFE_API = w.GCITY_SAFE_API || {};

    // #11.1 — Guarded safeFetch compatibility wrapper
    w.GCITY_SAFE_API.safeFetch = async function safeFetch(url, opts) {
      var raw = String(url || '');
      var normalized = normalizeEndpoint(raw);
      var registryPath = normalizeEndpointForRegistry(raw);

      if (raw.indexOf('workers.dev') >= 0) {
        return { success: false, ok: false, error: 'blocked_raw_worker_endpoint' };
      }

      if (normalized && normalized.indexOf('/apps/gcity-api/') === 0) {
        var method = String((opts && opts.method) || 'GET').toUpperCase();
        if (method !== 'GET' || (opts && opts.body)) {
          return { success: false, ok: false, error: 'blocked_app_proxy_execution_bypass' };
        }

        try {
          var response = await fetch(normalized, opts || {});
          return await normalizeResponse(response, {
            delivery_type: 'preview',
            result_surface: 'panel',
          });
        } catch (error) {
          return { success: false, ok: false, error: error && error.message };
        }
      }

      if (registryPath && registryPath.indexOf('/api/') === 0) {
        var legacy = LEGACY_API_ALLOWLIST[registryPath];
        if (!legacy) {
          return {
            success: false,
            ok: false,
            error: 'blocked_unknown_legacy_api_route:' + registryPath,
          };
        }

        return w.GCityExecutor.execute(
          Object.assign({}, legacy, {
            requires_gfun: false,
            requires_entitlement: false,
            data: opts && opts.body ? safeJsonParse(opts.body) : {},
          })
        );
      }

      return {
        success: false,
        ok: false,
        error: 'blocked_noncanonical_fetch:' + (registryPath || normalized || raw),
      };
    };

    // #11.2 — GFun balance query helper
    w.GCITY_SAFE_API.getGFunBalance = function getGFunBalance(customerId) {
      return w.GCityExecutor.execute({
        api_family: 'core',
        canonical_endpoint: '/api/gfun/balance',
        delivery_type: 'preview',
        delivery_method: 'inline',
        engine_type: 'gfun_balance',
        result_surface: 'panel',
        requires_gfun: false,
        requires_entitlement: false,
        data: customerId ? { customerId: customerId } : {},
      });
    };

    // #11.3 — Email delivery compatibility helper
    w.GCITY_SAFE_API.deliverEmail = function deliverEmail(payload) {
      return w.GCityExecutor.execute({
        api_family: 'core',
        canonical_endpoint: '/api/deliver/email',
        delivery_type: 'preview',
        delivery_method: 'email',
        engine_type: 'delivery',
        result_surface: 'panel',
        requires_gfun: false,
        requires_entitlement: false,
        data: payload || {},
      });
    };

    // #11.4 — Entitlement compatibility helper
    w.GCITY_SAFE_API.checkEntitlement = function checkEntitlement(payload) {
      return w.GCityExecutor.execute({
        api_family: 'core',
        canonical_endpoint: '/api/entitlement/check',
        delivery_type: 'preview',
        delivery_method: 'inline',
        engine_type: 'entitlement',
        result_surface: 'panel',
        requires_gfun: false,
        requires_entitlement: false,
        data: payload || {},
      });
    };
  }

  // ---------------------------------------------------------------------------
  // #12 — Runtime globals and ready event
  // Final public facade. No duplicate boot lifecycle is introduced here.
  // ---------------------------------------------------------------------------
  function exposeRuntimeGlobals() {
    w.GCITY = w.GCITY || {};
    w.GCITY.integration = w.GCITY.integration || {};

    w.GCITY.executor = w.GCityExecutor;
    w.GCITY.safeFetch = w.GCITY_SAFE_API.safeFetch;

    w.GCITY.resolveEndpointBase = function resolveEndpointBase(family) {
      return FAMILY_ROUTE_PREFIX[normalizeApiFamily(family)] || FAMILY_ROUTE_PREFIX.core;
    };

    w.GCityAPI = Object.assign(w.GCityAPI || {}, {
      executor: w.GCityExecutor,
      safeFetch: w.GCITY_SAFE_API.safeFetch,
      getBalance: function getBalance(customerId) {
        return w.GCITY_SAFE_API.getGFunBalance(customerId || getCustomerId());
      },
      checkEntitlement: w.GCITY_SAFE_API.checkEntitlement,
      deliverEmail: w.GCITY_SAFE_API.deliverEmail,
    });

    try {
      w.dispatchEvent(
        new CustomEvent('gcity:api:ready', {
          detail: {
            source: 'gcity-api-integration.js',
            executor: true,
            delivery_envelope: true,
            app_proxy_only: true,
          },
        })
      );
    } catch (_) {}
  }
})(window, document);
